import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  IconButton,
  TableRow,
  TableCell,
  TextField,
  Typography,
  FormHelperText,
  Button
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";

import {
  getDifferentiatedValues,
  getUser,
  getUserIndex,
  isOnlyAndMainEmployee
} from "../../../../utils/HealthDeclarationForm.utils";

import { RecentHealthConditionClass } from "../../../../model/RecentHealthCondition.class";

import { selectHealthDeclarationOfUsers } from "../../../../redux/health-declaration/hdf.selectors";
import { handleUpdateHealthDeclaration } from "../../../../redux/health-declaration/hdf.actions";

import { questionnairesStyles, saveButton } from "../questionnaire.styles";

import { DatePickerComponent } from "../../date-picker/DatePicker.component";

export const RecentHealthConditionQuestionnaire = props => {
  const {
    container,
    username,
    tableCell,
    infoGrid,
    textFieldRed
  } = questionnairesStyles();
  const { id } = props;

  const [date, setDate] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [treatment, setTreatment] = useState("");
  const [
    nameAddressOfDoctorConsulted,
    setNameAddressOfDoctorConsulted
  ] = useState("");

  const [dateError, setDateError] = useState(false);
  const [diagnosisError, setDiagnosisError] = useState(false);
  const [treatmentError, setTreatmentError] = useState(false);
  const [
    nameAddressOfDoctorConsultedError,
    setNameAddressOfDoctorConsultedError
  ] = useState(undefined);

  const healthDeclarationOfUsers = useSelector(selectHealthDeclarationOfUsers);
  const dispatch = useDispatch();

  const { userId, key, subKey } = getDifferentiatedValues(id);

  const userIndex = getUserIndex(healthDeclarationOfUsers, userId);
  const user = getUser(healthDeclarationOfUsers, userId);

  useEffect(() => {
    user?.recentHealthCondition?.date &&
      setDate(user.recentHealthCondition.date);
    user?.recentHealthCondition?.diagnosis &&
      setDiagnosis(user.recentHealthCondition.diagnosis);
    user?.recentHealthCondition?.treatment &&
      setTreatment(user.recentHealthCondition.treatment);
    user?.recentHealthCondition?.nameAddressOfDoctorConsulted &&
      setNameAddressOfDoctorConsulted(
        user.recentHealthCondition.nameAddressOfDoctorConsulted
      );

    let validateSuccess = validateFields();

    if (user?.recentHealthCondition || validateSuccess) {
      validateSuccess = true;
    }

    props.handleRequiredFieldsError(key, subKey, !validateSuccess);
  }, []);

  useEffect(() => {
    validateFields();
  }),
    [date, diagnosis, treatment, nameAddressOfDoctorConsulted];

  const validateFields = () => {
    let validateSuccess = true;

    setDateError(!date);
    setDiagnosisError(!diagnosis);
    setTreatmentError(!treatment);
    setNameAddressOfDoctorConsultedError(!nameAddressOfDoctorConsulted);

    if (!date || !diagnosis || !treatment || !nameAddressOfDoctorConsulted) {
      validateSuccess = false;
    }

    return validateSuccess;
  };

  const updateUserHealthDeclaration = () => {
    let validationResult = validateFields();

    if (validationResult) {
      props.handleRequiredFieldsError(key, subKey, false);
      const recentHealthCondition = new RecentHealthConditionClass(
        date,
        diagnosis,
        treatment,
        nameAddressOfDoctorConsulted
      );
      const recentHealthObject = {};
      recentHealthObject[
        subKey
      ] = recentHealthCondition.getRecentHealthConditionDetails();

      const updatedUser = {
        ...user,
        ...recentHealthObject
      };

      healthDeclarationOfUsers[userIndex] = updatedUser;
      dispatch(handleUpdateHealthDeclaration(healthDeclarationOfUsers));
    }
  };

  return (
    <Box className={container}>
      <Typography className={username}>
        {isOnlyAndMainEmployee(healthDeclarationOfUsers) ? "" : user?.full_name}
        {isOnlyAndMainEmployee(healthDeclarationOfUsers)
          ? ""
          : user.person_type === "EMPLOYEE"
          ? "(Self)"
          : user.person_type === "SPOUSE"
          ? "(Spouse)"
          : "(Child)"}
      </Typography>

      <TableRow>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Date</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <DatePickerComponent
                key={date}
                date={date}
                onDateChange={event => {
                  setDate(event);
                }}
              />
            </Grid>
            {dateError && (
              <FormHelperText style={{ color: "red" }}>
                This field is mandatory.
              </FormHelperText>
            )}
          </Grid>
        </TableCell>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Diagnosis</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={diagnosisError}
                helperText={diagnosisError && "This field is mandatory."}
                onChange={event => {
                  setDiagnosis(event.target.value);
                }}
                value={diagnosis}
              />
            </Grid>
          </Grid>
        </TableCell>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Treatment</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={treatmentError}
                helperText={treatmentError && "This field is mandatory."}
                onChange={event => {
                  setTreatment(event.target.value);
                }}
                value={treatment}
              />
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Name & Address Of Doctor Consulted</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={nameAddressOfDoctorConsultedError}
                helperText={
                  nameAddressOfDoctorConsultedError &&
                  "This field is mandatory."
                }
                onChange={event => {
                  setNameAddressOfDoctorConsulted(event.target.value);
                }}
                value={nameAddressOfDoctorConsulted}
              />
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      <Button
        variant="outlined"
        sx={{ ...saveButton }}
        onClick={updateUserHealthDeclaration}
      >
        Save
      </Button>
    </Box>
  );
};
